/**
 * Customer Accounts Admin Tab.
 * - List of registered customer accounts (search by name/email/mobile)
 * - Click → drawer with full activity timeline + bookings
 *
 * Uses the `api` function-style wrapper from PanelAuthContext: api(method, path, body)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Users, Search, X, Phone, Mail, MapPin, Calendar, Activity, Receipt, RefreshCw, Loader2, ArrowRight, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useDeleteConfirm } from '../../components/DeleteConfirmDialog';

const fmtDate = (s) => s ? new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const EVENT_LABELS = {
  signup: '🎉 Signed up',
  login: '🔐 Logged in',
  page_view: '📄 Page view',
  calculator_open: '🧮 Opened calculator',
  calculator_open_gated: '🔒 Calculator (gated)',
  pest_select: '🐛 Selected pest',
  size_select: '🏠 Selected size',
  lead_submit: '📨 Submitted lead',
  booking_create: '✅ Booking',
  payment_success: '💳 Payment',
  password_reset: '🔑 Password reset',
  profile_update: '✏️ Profile update',
  chat_message: '💬 Chat',
  coupon_apply: '🏷️ Coupon applied',
};

export default function CustomerAccountsTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const { confirm: confirmDelete } = useDeleteConfirm();

  const handleDelete = async (row, e) => {
    e.stopPropagation();
    const ok = await confirmDelete({ module: 'customer_accounts', id: row.id, name: row.name || row.email });
    if (ok) load(q);
  };

  const load = useCallback(async (search = '') => {
    setLoading(true);
    try {
      const path = `/admin/customer-accounts${search ? `?q=${encodeURIComponent(search)}` : ''}`;
      // The wrapper prefixes /api/panel automatically — but our route is also under /api/panel.
      const res = await api('GET', path);
      setRows(res.data || []);
    } catch (e) { /* */ } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const onSearch = (e) => {
    e.preventDefault();
    load(q);
  };

  return (
    <div data-testid="customer-accounts-tab">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Customer Accounts</h2>
          <p className="text-sm text-slate-500 mt-0.5">Registered website users · activity tracking · lead intelligence</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(q)} data-testid="ca-refresh">
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      <form onSubmit={onSearch} className="bg-white rounded-xl border border-slate-200 p-3 mb-4 flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, or mobile…"
            className="pl-9 h-10"
            data-testid="ca-search-input"
          />
        </div>
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Search</Button>
      </form>

      {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="border-2 border-dashed border-slate-200 rounded-2xl p-10 text-center bg-white">
          <Users size={36} className="mx-auto text-slate-300 mb-3" />
          <div className="font-semibold text-slate-700">No customer accounts yet</div>
          <div className="text-sm text-slate-500 mt-1">Accounts appear here when website visitors sign up to use the calculator.</div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3 text-center">Activity</th>
                <th className="px-4 py-3 text-center">Bookings</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3">Last login</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.city || '—'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs flex items-center gap-1 text-slate-700"><Mail size={11} /> {r.email}</div>
                    <div className="text-xs flex items-center gap-1 text-slate-500 mt-0.5"><Phone size={11} /> {r.mobile}</div>
                  </td>
                  <td className="px-4 py-3 text-center font-mono text-sm">{r.activity_count || 0}</td>
                  <td className="px-4 py-3 text-center font-mono text-sm">{r.bookings_count || 0}</td>
                  <td className="px-4 py-3 text-xs">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-3 text-xs">{fmtDate(r.last_login_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setSelected(r)} data-testid={`ca-view-${r.id}`}>
                        View <ArrowRight size={13} className="ml-1" />
                      </Button>
                      <button
                        onClick={(e) => handleDelete(r, e)}
                        className="w-8 h-8 rounded-md border border-red-200 bg-red-50 hover:bg-red-100 text-red-600 inline-flex items-center justify-center"
                        title="Move customer to Recycle Bin"
                        data-testid={`ca-delete-${r.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Drawer open={!!selected} onClose={() => setSelected(null)} account={selected} api={api} />
    </div>
  );
}

function Drawer({ open, onClose, account, api }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!open || !account) { setDetail(null); return undefined; }
    setLoading(true);
    api('GET', `/admin/customer-accounts/${account.id}`)
      .then(r => { if (alive) setDetail(r.data); })
      .catch(() => { if (alive) setDetail(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, account, api]);

  if (!open || !account) return null;

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/40" />
      <div className="w-full max-w-2xl bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10">
          <div>
            <h3 className="font-bold text-slate-900">{account.name}</h3>
            <p className="text-xs text-slate-500">{account.email} · +91 {account.mobile}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="ca-drawer-close"><X size={18} /></button>
        </div>
        <div className="p-5">
          {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
          {detail && (
            <>
              {/* Stat strip */}
              <div className="grid grid-cols-4 gap-3 mb-5">
                <Stat label="Total events" value={detail.stats?.total_events || 0} />
                <Stat label="Bookings" value={detail.stats?.total_bookings || 0} />
                <Stat label="Login count" value={detail.login_count || 0} />
                <Stat label="Status" value={detail.status || 'active'} />
              </div>

              {detail.address && (
                <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm">
                  <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Address</span>
                  <div className="flex items-start gap-1.5 mt-1 text-slate-800"><MapPin size={14} className="mt-0.5 text-slate-400" /> {detail.address}</div>
                </div>
              )}

              {/* Bookings */}
              {(detail.bookings || []).length > 0 && (
                <div className="mb-5">
                  <h4 className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-1.5"><Receipt size={14} /> Bookings</h4>
                  <div className="space-y-1.5">
                    {detail.bookings.map(b => (
                      <div key={b.id} className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex items-center justify-between text-xs">
                        <div>
                          <span className="font-mono text-slate-500">{b.booking_code || b.id?.slice(0, 8)}</span>
                          <span className="ml-2 font-semibold text-slate-900">{b.service_type || b.pest_type}</span>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-emerald-700">₹{(b.final_amount || b.amount || 0).toLocaleString()}</div>
                          <div className="text-[10px] text-slate-400">{fmtDate(b.created_at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activity */}
              <div>
                <h4 className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-1.5"><Activity size={14} /> Activity Timeline</h4>
                <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
                  {(detail.activities || []).length === 0 && (
                    <div className="px-3 py-6 text-center text-sm text-slate-400">No tracked activity yet</div>
                  )}
                  {(detail.activities || []).map(e => (
                    <div key={e.id} className="px-3 py-2 flex flex-wrap items-center gap-2 text-xs">
                      <span className="font-medium">{EVENT_LABELS[e.event_type] || e.event_type}</span>
                      {e.path && <code className="bg-slate-100 px-1 py-0.5 rounded text-[10px]">{e.path}</code>}
                      {e.payload && Object.keys(e.payload).length > 0 && (
                        <span className="text-[10px] text-slate-400">{Object.entries(e.payload).slice(0, 3).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(', ')}</span>
                      )}
                      <span className="ml-auto text-[10px] text-slate-400">{fmtDate(e.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-lg font-bold text-slate-900">{value}</div>
    </div>
  );
}
